import React, { useState, useEffect } from 'react';
import { IconLocation, IconPhone, IconCheck, IconX, IconEye, IconSearch } from '../components/Icons';
import { collection, query, orderBy, onSnapshot, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

const CATEGORY_EMOJI = { Active: '✅', "On Break": '⏸️', Offline: '⚫' };

export default function DeliveryBoys() {
  const [search, setSearch] = useState('');
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveTimers, setLiveTimers] = useState({});

  // Fetch delivery boys & transporters from Firestore
  useEffect(() => {
    const q = query(collection(db, 'users'), orderBy('joinDate', 'desc'));
    const unsub = onSnapshot(q, snap => {
      const data = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(d => d.role === 'deliveryboy' || d.role === 'Delivery Boy' || d.role === 'transporter');
      setDeliveryBoys(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Live timers for active delivery boys
  useEffect(() => {
    const iv = setInterval(() => {
      setLiveTimers(t => {
        const n = { ...t };
        deliveryBoys.filter(d => d.status === 'Active').forEach(d => {
          n[d.id] = (n[d.id] || 0) + 1;
        });
        return n;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [deliveryBoys]);

  // Delete demo delivery boys (status === 'Pending')
  const deleteDemoData = async () => {
    const demoDocs = deliveryBoys.filter(d => d.status === 'Pending');
    for (const d of demoDocs) {
      await deleteDoc(doc(db, 'users', d.id));
    }
    alert('Demo delivery boys deleted');
  };

  const changePassword = async (id, current) => {
    const newPass = window.prompt("नवीन पासवर्ड टाका (Enter New Password):", current);
    if (newPass && newPass !== current) {
      try {
        await updateDoc(doc(db, 'users', id), { adminPassword: newPass });
        alert("पासवर्ड बदलला आहे!");
      } catch (e) {
        alert("Error: " + e.message);
      }
    }
  };

  const updateStatus = async (boyObj, status) => {
    try {
      const updateData = { status, isApproved: status === 'Active' };
      if (status === 'Active') {
        if (boyObj.role === 'transporter') {
          updateData.transporterApproved = true;
        } else {
          updateData.deliveryboyApproved = true;
        }
        if (!boyObj.adminPassword) {
          updateData.adminPassword = Math.floor(100000 + Math.random() * 900000).toString();
        }
      }
      await updateDoc(doc(db, 'users', boyObj.id), updateData);
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const filtered = deliveryBoys.filter(d => {
    const matchSearch = d.name?.toLowerCase().includes(search.toLowerCase()) ||
      d.phone?.includes(search) || d.vehicleNumber?.toLowerCase().includes(search.toLowerCase());
    return matchSearch;
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="search-bar" style={{ flex: 1, minWidth: 200 }}>
          <IconSearch size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <input
            placeholder="नाव, फोन किंवा गाडी नंबर शोधा | Search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {/* Delete demo data button */}
        <button className="btn btn-danger btn-sm" onClick={deleteDemoData}>Delete Demo Data</button>
      </div>

      {/* Header stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {[
          { l: 'Total Fleet', v: deliveryBoys.length, c: 'var(--text-primary)', icon: '👥' },
          { l: 'Active Now', v: deliveryBoys.filter(d => d.status === 'Active').length, c: 'var(--success)', icon: '🟢' },
          { l: 'On Break', v: deliveryBoys.filter(d => d.status === 'On Break').length, c: 'var(--warning)', icon: '⏸️' },
          { l: 'Offline', v: deliveryBoys.filter(d => d.status === 'Offline').length, c: 'var(--text-muted)', icon: '⚫' },
          { l: "Today's Orders", v: deliveryBoys.reduce((a, d) => a + (d.ordersToday || 0), 0), c: 'var(--info)', icon: '📦' },
        ].map((s, i) => (
          <div key={i} style={{ flex: 1, padding: '12px 16px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, textAlign: 'center' }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: s.c }}>{s.v}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.l}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading delivery fleet...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 16 }}>
          {filtered.map(boy => (
            <div key={boy.id} className="card" style={{
              cursor: 'pointer',
              borderColor: boy.status === 'Active' ? 'rgba(34,197,94,0.3)' :
                boy.status === 'On Break' ? 'rgba(245,158,11,0.3)' : 'var(--border)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                    background: `hsl(${(boy.name?.charCodeAt(0) * 25) % 360}, 55%, 45%)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: 16, fontWeight: 700, position: 'relative'
                  }}>
                    {boy.name?.[0]}
                    {boy.status === 'Active' && (
                      <span style={{ position: 'absolute', bottom: 0, right: 0, width: 12, height: 12, borderRadius: '50%', background: 'var(--success)', border: '2px solid var(--bg-card)' }} />
                    )}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>
                      {boy.name || 'New Driver'}
                      <span style={{ fontSize: 10, fontWeight: '800', background: boy.role === 'transporter' ? 'rgba(139,92,246,0.1)' : 'rgba(59,130,246,0.1)', color: boy.role === 'transporter' ? '#8B5CF6' : 'var(--info)', padding: '2px 6px', borderRadius: 4, marginLeft: 8 }}>
                        {boy.role === 'transporter' ? '🏍️ Transporter' : '🛵 Delivery Partner'}
                      </span>
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>Joined: {boy.joinDate?.toDate?.()?.toLocaleDateString('en-US') || 'Recent'}</div>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 500 }}>
                  <IconLocation size={14} style={{ color: boy.status === 'Active' ? 'var(--success)' : 'var(--text-muted)' }} />
                  {boy.status === 'Active' ? 'Online' : 'Offline'}
                </div>
                <span className={`badge ${boy.status === 'Active' ? 'success' : boy.status === 'On Break' ? 'warning' : 'muted'}`}>{boy.status || 'Pending'}</span>
              </div>
              
              {boy.status === 'Active' && boy.adminPassword && (
                <div style={{ padding: '8px 12px', background: 'rgba(59, 130, 246, 0.1)', border: '1px dashed var(--info)', borderRadius: 8, fontSize: 13, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>Login Password:</span>
                    <span style={{ color: 'var(--info)', fontWeight: '800', letterSpacing: 2 }}>{boy.adminPassword}</span>
                  </div>
                  <button 
                    onClick={() => changePassword(boy.id, boy.adminPassword)}
                    className="btn btn-secondary btn-sm"
                    style={{ padding: '4px 8px', fontSize: 10 }}
                  >
                    📝 Edit
                  </button>
                </div>
              )}
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <span>📞 {boy.phone || 'N/A'}</span>
                <span>📏 Zone: {boy.village || boy.zone || 'Sangola'}</span>
                {boy.role === 'transporter' && (
                  <span style={{ color: '#8B5CF6', fontWeight: '700' }}>
                    🚙 {boy.vehicleType}: {boy.vehicleNumber}
                  </span>
                )}
              </div>
              {boy.currentOrder && (
                <div style={{ padding: '8px 12px', background: 'rgba(255,107,53,0.08)', border: '1px solid rgba(255,107,53,0.2)', borderRadius: 8, fontSize: 12, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ animation: 'pulse 1.5s infinite', display: 'inline-block', width: 6, height: 6, background: 'var(--primary)', borderRadius: '50%' }} />
                  <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Active: {boy.currentOrder}</span>
                  {liveTimers[boy.id] && (
                    <span style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}>{Math.floor(liveTimers[boy.id] / 60)}m {liveTimers[boy.id] % 60}s</span>
                  )}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                {[
                  { l: 'Jobs Done', v: boy.completedTasks || boy.ordersToday || 0 },
                  { l: 'Cash (COD)', v: `₹${boy.cashInHand || 0}`, c: 'var(--success)' },
                  { l: 'Earnings', v: `₹${boy.earnings || 0}`, c: 'var(--info)' },
                  { l: 'Rating', v: `⭐${boy.rating || '5.0'}` },
                ].map((d, i) => (
                  <div key={i} style={{ background: 'var(--bg)', padding: '8px', borderRadius: 7, textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{d.l}</div>
                    <div style={{ fontWeight: 700, fontSize: 11, color: d.c || 'inherit' }}>{d.v}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                {boy.cashInHand > 0 && (
                  <button 
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (window.confirm(`${boy.name} कडून ₹${boy.cashInHand} जमा झाले आहेत? (Settle Cash)`)) {
                        try {
                          await updateDoc(doc(db, 'users', boy.id), { cashInHand: 0 });
                          alert("कॅश हिशोब पूर्ण झाला!");
                        } catch (e) {
                          alert("Error: " + e.message);
                        }
                      }
                    }}
                    className="btn btn-secondary btn-sm"
                    style={{ flex: 1, borderColor: 'var(--success)', color: 'var(--success)', background: 'rgba(16, 185, 129, 0.1)', fontSize: 10 }}
                  >
                    💰 Cash जमा
                  </button>
                )}

                {boy.earnings > 0 && (
                  <button 
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (window.confirm(`${boy.name} ला ₹${boy.earnings} कमिशन दिले आहे? (Settle Earnings)`)) {
                        try {
                          await updateDoc(doc(db, 'users', boy.id), { earnings: 0 });
                          alert("कमिशन हिशोब पूर्ण झाला!");
                        } catch (e) {
                          alert("Error: " + e.message);
                        }
                      }
                    }}
                    className="btn btn-secondary btn-sm"
                    style={{ flex: 1, borderColor: 'var(--info)', color: 'var(--info)', background: 'rgba(59, 130, 246, 0.1)', fontSize: 10 }}
                  >
                    💸 Earnings द्या
                  </button>
                )}
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px dashed var(--border)' }}>
                {boy.status === 'Pending' ? (
                  <>
                    <button onClick={() => updateStatus(boy, 'Active')} className="btn btn-success" style={{ flex: 1, padding: '8px' }}>Approve</button>
                    <button onClick={() => updateStatus(boy, 'Rejected')} className="btn btn-danger" style={{ flex: 1, padding: '8px' }}>Reject</button>
                  </>
                ) : boy.status === 'Active' || boy.status === 'On Break' ? (
                  <button onClick={() => updateStatus(boy, 'Blocked')} className="btn btn-danger" style={{ flex: 1, padding: '8px' }}>Block Account</button>
                ) : (
                  <button onClick={() => updateStatus(boy, 'Active')} className="btn btn-success" style={{ flex: 1, padding: '8px' }}>Unblock Account</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
